import { Button } from '@/components/ui/button';
import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetPolicySummaryQueryKey,
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiDeclineTransactionMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Select } from '@/lib/pages/forms/components';
import { Form } from '@/components/ui/form';
import type { Selectable } from '@/lib/pages/forms/components/Select';
import { DialogClose } from '@/components/ui/dialog';

const DECLINE_OPTIONS: Array<Selectable<string>> = [
  'Pricing/competition',
  'Coverage',
  'No response to request for information',
  'Loyalty to incumbent broker/carrier',
].map((d) => ({ label: d, value: d }));

type DeclineForm = {
  option: string;
};

export const Decline = ({
  accountId,
  title,
}: {
  accountId: string;
  title: string;
}) => {
  const qc = useQueryClient();
  const permissionsQk = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: { account_id: accountId },
  });
  const summariesQk = glacierPolicyApiGetPolicySummaryQueryKey();
  const policyQk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const transfer = useMutation({
    ...glacierPolicyApiDeclineTransactionMutation(),
    onSuccess() {
      qc.refetchQueries({ queryKey: permissionsQk });
      qc.refetchQueries({ queryKey: summariesQk });
      qc.refetchQueries({ queryKey: policyQk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">{title}</code>
          </pre>
        ),
      });
    },
  });

  const form = useForm<DeclineForm>({
    defaultValues: {
      option: undefined,
    },
  });

  const { control, handleSubmit } = form;

  const onSubmit: SubmitHandler<DeclineForm> = (data) => {
    transfer.mutate({
      path: {
        account_id: accountId,
      },
      body: {
        reason: data.option,
      },
    });
  };
  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="pb-4 pt-4">
            <Select
              control={control}
              name="option"
              options={DECLINE_OPTIONS}
              label=""
              placeholder="Choose reason"
            />
          </CardContent>
          <CardFooter className="flex justify-end">
            <DialogClose asChild>
              <Button
                type="submit"
                className="bg-brand-green text-brand-0 transition hover:bg-brand-green hover:opacity-70 hover:duration-300"
              >
                Submit
              </Button>
            </DialogClose>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};
